import { getEffectiveFirebaseIdToken } from '@/features/impersonation/effectiveFirebaseToken'
import { useCompany } from '@/redux/company'
import { useUser } from '@/redux/user'
import { api } from '@/services/api'
import { createContext, createElement, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { AppearanceIndex, buildAppearanceIndex } from './signature'

export type OperatorAppearance = { id: string; name: string; color: string }
type AppearanceData = { operators: OperatorAppearance[]; canViewGroups: boolean; canManageGroups: boolean }
const AppearanceContext = createContext<{ data?: AppearanceData; isError: boolean; index: AppearanceIndex }>({
  isError: false,
  index: buildAppearanceIndex()
})

export function OperatorAppearanceProvider({ children }: { children: ReactNode }) {
  const {
    companyState: { selectedCompany }
  } = useCompany()
  const {
    userState: { userAuthenticated }
  } = useUser()
  const key = `${selectedCompany}:${userAuthenticated?.id}`
  const [state, setState] = useState<{ key: string; data?: AppearanceData; isError: boolean }>({
    key: '',
    isError: false
  })
  useEffect(() => {
    if (!selectedCompany || !userAuthenticated?.id) return
    const controller = new AbortController()
    let inFlight = false
    const load = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const token = await getEffectiveFirebaseIdToken()
        const response = await api.get('/users/operator-appearance', {
          params: { companyId: selectedCompany },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        })
        if (!controller.signal.aborted) setState({ key, data: response.data, isError: false })
      } catch {
        if (!controller.signal.aborted) setState({ key, isError: true })
      } finally {
        inFlight = false
      }
    }
    void load()
    const timer = setInterval(() => {
      if (!document.hidden) void load()
    }, 60000)
    return () => {
      controller.abort()
      clearInterval(timer)
    }
  }, [key, selectedCompany, userAuthenticated?.id])
  const current = state.key === key ? state : { data: undefined, isError: false }
  const index = useMemo(() => buildAppearanceIndex(current.data?.operators), [current.data?.operators])
  const value = useMemo(
    () => ({ data: current.data, isError: current.isError, index }),
    [current.data, current.isError, index]
  )
  return createElement(AppearanceContext.Provider, { value }, children)
}

export function useOperatorAppearance() {
  return useContext(AppearanceContext)
}
